import { useState, useEffect, useRef } from "react";
import axios from 'axios';
import StudentCardPreview from '../student/StudentCardPreview';
const { buildQuestionLookup, isQuestionVisible } = require("../shared/progress");
const { exchangeDocumentBaseName } = require("../shared/exchangeDocuments");

const IDENTITY_PHOTO_QUESTION_KEYS = new Set(["photoidentite", "photodidentite"]);

function normalizeUploadQuestionName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function isIdentityPhotoQuestion(questionInfo) {
  const questionKeys = [
    normalizeUploadQuestionName(questionInfo?.nom),
    normalizeUploadQuestionName(questionInfo?.etiquette)
  ];
  return questionKeys.some((key) => IDENTITY_PHOTO_QUESTION_KEYS.has(key));
}

function revokeBlobUrl(url) {
  if (typeof url === "string" && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
}


function QuestionUpload({ questionInfo, etudiant, updateQuestionValue, sousCritereIndex, questionIndex, getNumberFiliereActif, getNumberCritereActif, data, Lang, setQuestionValueToTrue }) {

  /* ------------------------------------------------------------ STATE ------------------------------------------------------------ */

  const documentBase = data?.configuration === 'Echange' ? exchangeDocumentBaseName(questionInfo.nom) : questionInfo.nom;
  const fileName = documentBase + '.png';
  const [fileURL, setFileURL] = useState(null);
  const [pendingIdentityPhotoFile, setPendingIdentityPhotoFile] = useState(null);
  const [pendingIdentityPhotoPreviewURL, setPendingIdentityPhotoPreviewURL] = useState(null);

  const [uploadError, setUploadError] = useState('');
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const previewRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const confirmButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);

  const currentPeriod = data?.data?.[getNumberFiliereActif];
  const questionLookup = buildQuestionLookup(currentPeriod);
  const isIdentityPhoto = isIdentityPhotoQuestion(questionInfo);
  const isIdentityPhotoConfirmOpen = isIdentityPhoto && Boolean(pendingIdentityPhotoFile && pendingIdentityPhotoPreviewURL);
  let visible = isQuestionVisible(questionInfo, {
    dataFiltres: currentPeriod?.filtres,
    questionLookup
  });
  const questions = data.data[getNumberFiliereActif].criteres[getNumberCritereActif].sousCriteres[sousCritereIndex].questions;


  /* ------------------------------------------------------------ COMPORTEMENT ------------------------------------------------------------ */

  const addItemToToken = async (token, newItem) => {
    try {
      // eslint-disable-next-line
      const response = await axios.post('/backend/api/add-item-to-token', {
        token,
        newItem
      });

      //console.log("token");
      //console.log(token);
      //console.log(newItem);

      //console.log(response);
    } catch (error) {
      //console.error('Erreur lors de l\'ajout de l\'élément:', error.response?.data || error.message);
    }
  };

  //Vérifie si un ficher à déjà été Upload pour cet étudiant
  useEffect(() => {
    let active = true;
    axios.get(`/backend/check-file`, {
      params: {
        etudiant: etudiant,
        fileName: fileName
      }
    })
      .then(response => {
        if (!active) return;
        //console.log(response.data);

        //console.log('File exists:', response.data.exists);
        if (response.data.exists) {
          updateQuestionValue(sousCritereIndex, questionIndex, response.data.exists);
          setFileURL(`/backend/static/${encodeURIComponent(etudiant)}/${encodeURIComponent(fileName)}`);

        } else {
          updateQuestionValue(sousCritereIndex, questionIndex, "");
          setFileURL(null);
        }
      })
      .catch(error => {
        //console.error('Error checking file:', error);
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etudiant, fileName]);

  useEffect(() => {
    if (fileURL && previewRef.current && shouldScrollRef.current) {
      previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      shouldScrollRef.current = false;
    }
  }, [fileURL]);

  useEffect(() => {
    return () => {
      revokeBlobUrl(pendingIdentityPhotoPreviewURL);
    };
  }, [pendingIdentityPhotoPreviewURL]);

  useEffect(() => {
    if (!isIdentityPhotoConfirmOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("uploadConfirmLock");

    if (confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableButtons = [cancelButtonRef.current, confirmButtonRef.current]
        .filter(Boolean)
        .filter((button) => !button.disabled);

      if (focusableButtons.length === 0) {
        event.preventDefault();
        return;
      }

      const currentIndex = focusableButtons.indexOf(document.activeElement);

      if (event.shiftKey) {
        if (currentIndex <= 0) {
          event.preventDefault();
          focusableButtons[focusableButtons.length - 1].focus();
        }
        return;
      }

      if (currentIndex === -1 || currentIndex === focusableButtons.length - 1) {
        event.preventDefault();
        focusableButtons[0].focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove("uploadConfirmLock");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isIdentityPhotoConfirmOpen]);

  const resetPendingIdentityPhoto = (options = {}) => {
    const clearInput = options.clearInput !== false;
    setPendingIdentityPhotoFile(null);
    setPendingIdentityPhotoPreviewURL((current) => {
      revokeBlobUrl(current);
      return null;
    });
    if (clearInput && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = async (event) => {
    //console.log("abc");
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      setUploadError('');
      //console.log("abcd");
      const fileSizeInMB = selectedFile.size / (1024 * 1024);
      if (fileSizeInMB > 1.5) {
        setIsErrorVisible(true);
        event.target.value = "";
        return;
      }

      setIsErrorVisible(false);
      let convertedFile = selectedFile;

      if (isIdentityPhoto && convertedFile) {
        const localPreviewUrl = URL.createObjectURL(convertedFile);
        setPendingIdentityPhotoFile(convertedFile);
        setPendingIdentityPhotoPreviewURL((current) => {
          revokeBlobUrl(current);
          return localPreviewUrl;
        });
        return;
      }

      if (convertedFile) {
        //console.log("abcde");
        //console.log("!!!SUCCESS!!!");
        //console.log(questionInfo.id);
        const accepted = await handleFileUpload(convertedFile);
        if (accepted) {
          shouldScrollRef.current = true;
          setTimeout(() => {
            loadImageAsBlob();
          }, 10);
        } else {
          event.target.value = "";
        }
      }
    }
  };
  const loadImageAsBlob = async () => {
    const imageUrl = `/backend/static/${encodeURIComponent(etudiant)}/${encodeURIComponent(fileName)}`;

    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error("Impossible de charger l'image.");

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      setFileURL(blobUrl);
    } catch (error) {
      //console.error("Erreur lors du chargement de l'image :", error);
    }

    return () => {
      if (fileURL) URL.revokeObjectURL(fileURL); // Nettoyage pour éviter les fuites mémoire
    };
  };

  const handleValidatePendingIdentityPhoto = async () => {
    if (!pendingIdentityPhotoFile) {
      return;
    }

    const accepted = await handleFileUpload(pendingIdentityPhotoFile);
    if (!accepted) {
      return;
    }

    resetPendingIdentityPhoto();
    shouldScrollRef.current = true;
    await loadImageAsBlob();
  };

  const handleCancelPendingIdentityPhoto = () => {
    resetPendingIdentityPhoto();
  };

  const handleFileUpload = async (selectedFile) => {
    if (!selectedFile) {
      alert('Please select a file first!');
      return false;
    }

    setUploadError('');
    setIsUploading(true);
    setUploadProgress(0);

    // Extraire l'extension du fichier (ex: .pdf, .png, .jpg)
    const fileExtension = selectedFile.name.split('.').pop(); // Récupère la dernière partie après le point

    // Construire le nouveau nom du fichier
    const newFileName = `${documentBase}.${fileExtension}`;

    const formData = new FormData();
    formData.append("file", selectedFile, newFileName);
    formData.append('etudiant', etudiant);

    const buildAvisWarning = (validation) => {
      const expectedYear = validation?.expectedYear ? ` ${validation.expectedYear}` : '';
      const unreadable = validation?.readable === false;
      if (Lang === "en") {
        return unreadable
          ? `We couldn't read the document (scan/rotation). Please make sure it is a tax notice${expectedYear}. Click OK to confirm it matches the request, or Cancel to upload another file.`
          : `The document doesn't look like a tax notice${expectedYear}. Click OK to confirm it matches the request, or Cancel to upload another file.`;
      }
      return unreadable
        ? `Impossible de lire le document (scan/rotation). Merci de vérifier qu'il s'agit bien de l'avis d'imposition${expectedYear}. Cliquez sur OK pour confirmer, ou Annuler pour déposer un autre fichier.`
        : `Nous n'arrivons pas à déterminer que le document est un avis d'imposition${expectedYear}. Vous pouvez tout de même cliquer sur OK pour confirmer la validité du document, Vous pouvez également cliquer sur Annuler pour déposer un autre fichier.`;
    };

    const confirmAvisUpload = async (pendingFileName) => {
      const response = await axios.post('/backend/confirm-avis-upload', {
        etudiant,
        pendingFileName,
        questionName: questionInfo.nom
      });
      return response?.data?.success === true;
    };

    const cancelAvisUpload = async (pendingFileName) => {
      try {
        await axios.post('/backend/cancel-avis-upload', {
          etudiant,
          pendingFileName,
          questionName: questionInfo.nom
        });
      } catch {
      }
    };

    try {
      const response = await axios.post(data?.configuration === 'Echange' ? `/backend/upload?token=${encodeURIComponent(etudiant)}` : '/backend/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (event) => {
          if (!event.total) return;
          const nextProgress = Math.min(100, Math.round((event.loaded / event.total) * 100));
          setUploadProgress(nextProgress);
        }
      });

      const validation = response?.data?.validation;
      let accepted = true;
      setUploadProgress(100);
      if (validation) {
        if (validation.valid) {
          updateQuestionValue(sousCritereIndex, questionIndex, true, true, false);
        } else {
          const confirmed = window.confirm(buildAvisWarning(validation));
          if (confirmed) {
            const pendingFileName = response?.data?.pendingFileName;
            if (pendingFileName) {
              const confirmedUpload = await confirmAvisUpload(pendingFileName);
              accepted = confirmedUpload;
            } else {
              accepted = false;
            }
            if (accepted) {
              updateQuestionValue(sousCritereIndex, questionIndex, true, false, true);
            }
          } else {
            accepted = false;
            const pendingFileName = response?.data?.pendingFileName;
            if (pendingFileName) {
              await cancelAvisUpload(pendingFileName);
            }
          }
        }
      }

      if (!accepted) {
        return false;
      }

      //console.log("hf" + etudiant + "/" + fileName);
      await addItemToToken(etudiant, newFileName);

      await convertDocument(newFileName);
      if (!validation) {
        updateQuestionValue(sousCritereIndex, questionIndex, true);
      }
      //console.log("nameFile: "+selectedFile.name)
      //console.log(etudiant);

      //console.log('Server response:', response.data);
    } catch (error) {
      const message = error.response?.data?.message;
      setUploadError(typeof message === 'string' && message ? message : Lang === 'en'
        ? 'The file could not be uploaded or converted. Please try again with a PNG, JPEG or PDF file (maximum 1.5 MB).'
        : 'Le fichier n’a pas pu être téléversé ou converti. Réessayez avec une image PNG/JPEG ou un PDF de 1,5 Mo maximum.');
      return false;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
    return true;
  };

  try {
    questions[questionIndex].visible = visible;
  } catch {

  }

  const convertDocument = async (uploadedFileName) => {
    await axios.post("/backend/convert-document", {
      etudiant,
      fileName: uploadedFileName,
      outputName: fileName
    });
  };


  /* ------------------------------------------------------------ AFFICHAGE ------------------------------------------------------------ */

  if (visible) {
    return (
      <div className="question">
        {isUploading && (
          <div className="uploadOverlay">
            <div className="uploadOverlayCard">
              <div className="uploadSpinner" />
              <div>{Lang === "en" ? "Uploading file..." : "Téléversement du fichier..."}</div>
              <div className="uploadProgress">{uploadProgress}%</div>
              <div className="uploadProgressBar">
                <div className="uploadProgressBarFill" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          </div>
        )}
        <label className="questionTitle">
          {questionInfo.etiquette} :{questionInfo.required === "required" ? " *" : ""}
        </label>
        {isIdentityPhotoConfirmOpen && (
          <div className="uploadConfirmOverlay">
            <div
              className="uploadConfirmDialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby={`upload-confirm-title-${questionInfo.id}`}
            >
              <h3 id={`upload-confirm-title-${questionInfo.id}`} className="uploadConfirmDialog__title">
                {Lang === "en" ? "Confirm identity photo" : "Votre future carte étudiante"}
              </h3>
              <p className="uploadConfirmDialog__text">
                {Lang === "en"
                  ? "Check the card preview before confirming the upload. You must choose one of the two actions below."
                  : "Merci de vérifier que la photo est conforme et correctement positionnée."}
              </p>
              {uploadError && <p role="alert" className="uploadStatus uploadStatus--file" style={{ color: "red" }}>{uploadError}</p>}
              <StudentCardPreview photoUrl={pendingIdentityPhotoPreviewURL} lang={Lang} modal />
              <div className="uploadConfirmActions uploadConfirmActions--modal">
                <button
                  type="button"
                  className="uploadConfirmButton uploadConfirmButton--secondary"
                  onClick={handleCancelPendingIdentityPhoto}
                  disabled={isUploading}
                  ref={cancelButtonRef}
                >
                  {Lang === "en" ? "Cancel upload" : "Annuler"}
                </button>
                <button
                  type="button"
                  className="uploadConfirmButton uploadConfirmButton--primary"
                  onClick={handleValidatePendingIdentityPhoto}
                  disabled={isUploading}
                  ref={confirmButtonRef}
                >
                  {Lang === "en" ? "Confirm upload" : "Je valide la photo"}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="questionUpload">
          <div className="questionvertical">
            <div className="fileInputWrapper">
              <input
                id={questionInfo.id}
                name={questionInfo.id}
                type={questionInfo.type}
                accept={questionInfo.accept}
                onChange={handleFileChange}
                className="fileInput"
                ref={fileInputRef}
              />
              <label htmlFor={questionInfo.id} className="fileInputLabel">
                {Lang === "en" ? "Choose a file" : "Choisir un fichier"}
              </label>
            </div>
            {uploadError && !isIdentityPhotoConfirmOpen && <p role="alert" className="uploadStatus uploadStatus--file" style={{ color: "red" }}>{uploadError}</p>}
            {isErrorVisible && (
              <p className="uploadStatus uploadStatus--file" style={{ color: "red" }}>
                {Lang === "en" ? "The file is too large!" : "Le fichier est trop volumineux !"}
              </p>
            )}
            {questionInfo.valideFormat === false && (
              <p className="uploadStatus uploadStatus--avis" style={{ color: questionInfo.valideFormatConfirm ? "#b45309" : "red" }}>
                {Lang === "en"
                  ? (questionInfo.valideFormatConfirm
                    ? "Document not recognized (confirmation saved)."
                    : "Document not recognized. Please check the file.")
                  : (questionInfo.valideFormatConfirm
                    ? "Document non reconnu."
                    : "Document non reconnu. Merci de vérifier le fichier.")}
              </p>
            )}
            {questionInfo.valideFormat === true && (
              <p className="uploadStatus uploadStatus--avis" style={{ color: "#15803d" }}>
                {Lang === "en"
                  ? "Document recognized as valid."
                  : "Document reconnu comme valide."}
              </p>
            )}
            {fileURL && (
              <div ref={previewRef}>
                {isIdentityPhoto ? (
                  <StudentCardPreview photoUrl={fileURL} lang={Lang} />
                ) : (
                  <img src={fileURL} alt="File preview" />
                )}
              </div>
            )}
          </div>
          <div className="QuestionHelpUpload" dangerouslySetInnerHTML={{ __html: questionInfo.QuestionHelp }} />
        </div>
      </div>
    );
  } else {
    return;
  }

}


export default QuestionUpload;
